import { Button, ButtonMode, Icon, ICONS, SortableWidgetProps, Widget } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { isEqual } from 'lodash';
import React, { forwardRef, ReactElement, useEffect, useState } from 'react';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ContentTypeFilter } from '../../../models';
import { useFiltersContext } from '../../Filters';
import { CONTENT_TYPE_FILTER_URL_KEY } from '../../FiltersBar/FiltersBar.logic';
import { QUICK_FILTERS, QuickFilter } from './CRQuickFilters.constants';
import styles from './CRQuickFilters.module.scss';

const cx = classNames.bind(styles);

const CRQuickFilters = (props: SortableWidgetProps, ref): ReactElement => {
  const [currentFilter, setCurrentFilter] = useState<string | undefined>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { filters } = useFiltersContext();

  useEffect(() => {
    if (!currentFilter) {
      return;
    }

    const selectedFilter = QUICK_FILTERS.find((x) => x.title === currentFilter);
    if (!selectedFilter) {
      return;
    }

    const filterSearchParams = createSearchParams(selectedFilter.filterTags);
    const filtersWithoutContentTypeParam = Array.from(searchParams).filter(
      (param) => param[0] !== CONTENT_TYPE_FILTER_URL_KEY
    );
    if (!isEqual(filtersWithoutContentTypeParam, Array.from(filterSearchParams))) {
      setCurrentFilter(undefined);
    }
  }, [filters, searchParams]);

  const handleClick = (filter: QuickFilter) => {
    const typeFilter = searchParams.get(CONTENT_TYPE_FILTER_URL_KEY) as ContentTypeFilter;

    if (currentFilter === filter.title) {
      setCurrentFilter(undefined);
      if (typeFilter) {
        searchParams.set(CONTENT_TYPE_FILTER_URL_KEY, typeFilter);
      }
      const newSearchParams = createSearchParams(searchParams);
      Object.keys(filter.filterTags).forEach((key) => newSearchParams.delete(key));
      setSearchParams(newSearchParams);
    } else {
      const filterTags = typeFilter
        ? {
            ...filter.filterTags,
            type: typeFilter,
          }
        : filter.filterTags;
      setCurrentFilter(filter.title);
      navigate({
        search: createSearchParams(filterTags).toString(),
      });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Widget headerTitle="Covenant Quick Filters" fixedHeight dataTestId="cr-quick-filters" ref={ref} {...props}>
      <div className={cx('body')} data-testid="cr-quick-content">
        {QUICK_FILTERS.map((filter, index) => (
          <div key={filter.title} className={cx('filter-link-container', `filter-link-container--item-${index}`)}>
            <Button
              className={cx('filter-action', { selected: currentFilter === filter.title })}
              mode={ButtonMode.SECONDARY}
              onClick={() => handleClick(filter)}
              ariaLabel={filter.title}
            >
              {currentFilter === filter.title && <Icon name={ICONS.CheckmarkSmall} height={10} width={10} />}
              <span className={cx('title')}>{filter.title}</span>
            </Button>
          </div>
        ))}
      </div>
    </Widget>
  );
};

export default forwardRef(CRQuickFilters);
